import React, { type FC, useCallback, useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router';

import { useDeleteVariant, useFontFileQueries, useSvgFileQueries, useTemplateQuery } from '@/api/queries';
import type { TemplateDetails } from '@/api/types';
import { Button, ButtonSize, ButtonType } from '@/common/button';
import { ConfirmationModal, ConfirmationModalType } from '@/common/confimation-modal';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '@/common/floating-panel';
import { IntaglioIcon } from '@/common/intaglio-icon';
import { ListTile } from '@/common/list-tile';
import { Navbar, NavbarType } from '@/common/navbar';
import { QuickHelp } from '@/common/quick-help';
import { UploadIcon } from '@/common/upload-icon';
import { loadFontFile } from '@/utils';

import { AddFrameModal } from './add-frame-modal';
import { FieldsPanel } from './fields-panel';
import { LoadingScreen } from './loading-screen';
import { PublishTemplateModal } from './publish-template-modal';
import { Stage } from './stage';
import { useTemplateEditorStore } from './store';
import { TemplateSettings } from './template-settings';
import { EditorViewMode } from './types';

export const TemplateEditor: FC = () => {
    const params = useParams<{ templateId: string }>();
    const templateId = Number.parseInt(params.templateId || '');
    if (Number.isNaN(templateId)) {
        console.error('Invalid template ID in url params:', params.templateId);
        return <Navigate to="/templates" replace={true} />;
    }

    const currentVariantIndex = useTemplateEditorStore((s) => s.currentVariantIndex);
    const editorViewMode = useTemplateEditorStore((s) => s.editorViewMode);
    const template = useTemplateEditorStore((s) => s.template);
    const setCurrentVariantIndex = useTemplateEditorStore((s) => s.setCurrentVariantIndex);
    const setEditorViewMode = useTemplateEditorStore((s) => s.setEditorViewMode);
    const setSelectedLayer = useTemplateEditorStore((s) => s.setSelectedLayer);
    const setTemplate = useTemplateEditorStore((s) => s.setTemplate);
    const addSvgFile = useTemplateEditorStore((s) => s.addSvgFile);

    const [isAddFrameModalOpen, setAddFrameModalOpen] = useState(false);
    const [isPublishModalOpen, setPublishModalOpen] = useState(false);
    const [initFetchComplete, setInitFetchComplete] = useState(false);
    const [isVariantDeleteConfirmationOpen, setIsVariantDeleteConfirmationOpen] = useState(false);
    const [variantForDeletion, setVariantForDeletion] = useState<number | null>(null);

    const { refetch: refetchTemplate } = useTemplateQuery(templateId, {
        onSuccess: (data: TemplateDetails) => {
            setTemplate(data);
        },
        staleTime: Number.POSITIVE_INFINITY,
        cacheTime: 0,
    });

    const { mutate: deleteVariant } = useDeleteVariant();

    const fonts = template?.latestVersion.variants.flatMap((v) => v.svgFile.availableFonts) || [];
    const fontFileQueryResults = useFontFileQueries(fonts, { enabled: !!template });
    const fontFilesLoaded = fontFileQueryResults.every((result) => result.isSuccess);

    const svgFiles = template?.latestVersion.variants.map((v) => v.svgFile.file) || [];
    const svgFileQueryResults = useSvgFileQueries(svgFiles, { enabled: !!template });
    const svgFilesLoaded = svgFileQueryResults.every((result) => result.isSuccess);

    const handleVariantChange = useCallback(
        (index: number) => {
            setCurrentVariantIndex(index);
            setSelectedLayer(null);
        },
        [setCurrentVariantIndex, setSelectedLayer],
    );

    const onVariantDeleteSuccss = useCallback(() => {
        const newVariants = template?.latestVersion.variants.filter((variant) => variant.id !== variantForDeletion);
        if (newVariants && template) {
            const newTemplate = {
                ...template,
                latestVersion: {
                    ...template.latestVersion,
                    variants: newVariants,
                },
            };
            setTemplate(newTemplate);
        }
        setVariantForDeletion(null);
        setIsVariantDeleteConfirmationOpen(false);
    }, [variantForDeletion, template, setTemplate]);

    useEffect(() => {
        if (variantForDeletion !== null) {
            setIsVariantDeleteConfirmationOpen(true);
        }
    }, [variantForDeletion]);

    useEffect(() => {
        if (!isVariantDeleteConfirmationOpen) {
            setVariantForDeletion(null);
        }
    }, [isVariantDeleteConfirmationOpen]);

    useEffect(() => {
        if (fontFilesLoaded && svgFilesLoaded && template) {
            const promises = [];
            for (const res of fontFileQueryResults) {
                const { url, file, font } = res.data;
                promises.push(loadFontFile(font, file));
            }

            for (const res of svgFileQueryResults) {
                addSvgFile(res.data.url, res.data.file);
            }
            Promise.all(promises).then(() => {
                setInitFetchComplete(true);
            });
        }
    }, [addSvgFile, fontFilesLoaded, fontFileQueryResults, svgFilesLoaded, svgFileQueryResults, template]);

    return (
        template && (
            <>
                <div
                    className={`flex flex-col min-h-0 h-screen w-full bg-black-50 ${initFetchComplete ? 'hidden' : ''}`}
                >
                    <LoadingScreen />
                </div>
                <div
                    className={`flex flex-col min-h-0 h-screen w-full bg-black-50 ${initFetchComplete ? '' : 'hidden'}`}
                >
                    <Navbar
                        type={NavbarType.LIGHT}
                        leading={
                            <div className="flex flex-row gap-4 items-center">
                                <Link to="/">
                                    <IntaglioIcon />
                                </Link>
                                <p className="text-sm text-white font-normal">Create Template</p>
                            </div>
                        }
                        center={<div />}
                        trailing={
                            <div className="flex flex-row gap-2.5 items-center">
                                <QuickHelp />
                                <div className="h-6 border-gray-100 border-l-[1px]" />
                                <Button
                                    title="Publish Template"
                                    buttonType={ButtonType.PRIMARY}
                                    buttonSize={ButtonSize.LARGE}
                                    onClick={() => setPublishModalOpen(true)}
                                />
                            </div>
                        }
                    />

                    <div className="flex min-h-0 flex-row h-full max-w-full min-w-full">
                        {editorViewMode === EditorViewMode.List && (
                            <FloatingPanel
                                className="pl-4 py-4 h-full w-64 flex-shrink-0 flex-grow-0"
                                type={FloatingPanelType.LIGHT}
                            >
                                <FloatingPanelItem className="px-4">
                                    <TemplateSettings />
                                </FloatingPanelItem>
                                <FloatingPanelItem className="px-4">
                                    <Button
                                        title="Upload Frames"
                                        className="w-full mt-4"
                                        buttonType={ButtonType.PRIMARY}
                                        buttonSize={ButtonSize.LARGE}
                                        leading={<UploadIcon className="h-5 w-5 stroke-white" />}
                                        onClick={() => setAddFrameModalOpen(true)}
                                    />
                                </FloatingPanelItem>
                                <FloatingPanelItem className="flex flex-col gap-2 h-full max-h-full min-h-0">
                                    <p className="text-sm text-white px-4">Frames</p>
                                    <div className="flex flex-col flex-grow gap-4 px-4 overflow-y-auto">
                                        {template.latestVersion.variants.map((variant, index) => {
                                            const isActive =
                                                template.latestVersion.variants[currentVariantIndex]?.id === variant.id;
                                            return (
                                                <ListTile
                                                    key={variant.id}
                                                    index={index + 1}
                                                    title={variant.name}
                                                    thumbnail={variant.thumbnail}
                                                    size={`${variant.svgFile.width}x${variant.svgFile.height}`}
                                                    isActive={isActive}
                                                    onDelete={() => setVariantForDeletion(variant.id)}
                                                    onClick={() => handleVariantChange(index)}
                                                />
                                            );
                                        })}
                                    </div>
                                </FloatingPanelItem>
                            </FloatingPanel>
                        )}

                        <div className="flex-grow flex-shrink h-full min-w-0 overflow-auto">
                            <div className="flex flex-col h-full min-w-0">
                                {editorViewMode === EditorViewMode.Grid && (
                                    <div className="p-4">
                                        <div className="inline-block bg-gray-150 p-4 rounded-md w-60">
                                            <TemplateSettings />
                                        </div>
                                    </div>
                                )}
                                <Stage />
                            </div>
                        </div>

                        <FieldsPanel />
                    </div>

                    <AddFrameModal
                        templateVersionId={template.latestVersion.id}
                        prevCount={template.latestVersion.variants.length}
                        isOpen={isAddFrameModalOpen}
                        setIsOpen={setAddFrameModalOpen}
                        onFrameAdded={refetchTemplate}
                    />

                    <PublishTemplateModal
                        template={template}
                        isOpen={isPublishModalOpen}
                        setIsOpen={setPublishModalOpen}
                        onCancel={() => { }}
                        onPublish={() => { }}
                    />

                    <ConfirmationModal
                        isOpen={isVariantDeleteConfirmationOpen}
                        setIsOpen={setIsVariantDeleteConfirmationOpen}
                        type={ConfirmationModalType.WARN}
                        confirmText="Delete"
                        cancelText="Cancel"
                        message="Delete Frame"
                        subMessage="Are you sure you want to delete this frame?"
                        onConfirm={() => {
                            deleteVariant(variantForDeletion as number, {
                                onSuccess: () => onVariantDeleteSuccss(),
                            });
                        }}
                    />
                </div>
            </>
        )
    );
};
